import { PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { COLOR_OPTIONS } from 'config/style';
import STTypo from 'core/shared/typo';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { useTranslation } from 'react-i18next';
import SvgClose from 'st-protector-icons/Minor/Close';
import { SelectionEmptyInfo } from './selection-empty-info';
import styles from './selection-action-button.module.css';

export interface SelectionActionButtonProps {
  selectionCount: number;
  buttonText: string;
  selectionsTitle?: string;
  emptySelectionsTitle?: string;
  selectionsLocalizationPath?: string;
  onClickButton: () => void;
  onClearSelections?: () => void;
  additionalButtons?: React.ReactNode;
  shouldRequireSelection?: boolean;
}

export const SelectionActionButton = ({
  additionalButtons,
  buttonText,
  emptySelectionsTitle,
  onClearSelections,
  onClickButton,
  selectionCount,
  selectionsLocalizationPath = 'components.selection_action_button.items_selected',
  selectionsTitle,
  shouldRequireSelection = true
}: SelectionActionButtonProps) => {
  const [t] = useTranslation();

  const showClearSelections = !!onClearSelections;

  const segmentTracking = useSegmentTracking();

  const handleClickButton = () => {
    onClickButton();
    if (buttonText.includes('pressure'))
      return segmentTracking.track('Disease risk - Historical disease pressure - Edit historical disease risk pressure');

    segmentTracking.track('Disease risk - Edit info - Edit additional information');
  };

  return (
    <div className={styles.selectionAction}>
      {selectionCount === 0 && shouldRequireSelection ? (
        <SelectionEmptyInfo title={emptySelectionsTitle} />
      ) : (
        <div className={styles.container}>
          <div className={styles.header}>
            {selectionsTitle && (
              <STTypo fontFamily='Noto Sans' fontWeight={400} color={COLOR_OPTIONS.NEUTRAL_40}>
                {selectionsTitle}
              </STTypo>
            )}
            <span className={styles.selections}>
              <STTypo fontFamily='Noto Sans' fontWeight={400} color={COLOR_OPTIONS.WHITE}>
                {t(selectionsLocalizationPath, { count: selectionCount })}
              </STTypo>
              {showClearSelections && (
                <div className={styles.clearSelections}>
                  <SvgClose
                    role='button'
                    aria-label='Clear selections'
                    fill={COLOR_OPTIONS.NEUTRAL_40}
                    width={12}
                    height={12}
                    className='selection-action-button__clear-selections'
                    onClick={onClearSelections}
                  />
                </div>
              )}
            </span>
          </div>
          <div className={styles.body}>
            <Button
              aria-label='Submit selections'
              type='primary'
              icon={<PlusOutlined />}
              onClick={handleClickButton}
              data-testid='selection-action-button'>
              {buttonText}
            </Button>
            {additionalButtons}
            {showClearSelections && (
              <button type='button' className={styles.cancelLink} onClick={onClearSelections}>
                {t('general.cancel')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
